import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

export const Button = ({ children, to, href, variant = "primary", className = "", testid, onClick, type }) => {
  const base = "group inline-flex min-h-12 max-w-full items-center justify-center gap-2 label text-center rounded-full px-5 sm:px-7 py-3.5 transition-colors duration-300 cursor-pointer";
  const styles = {
    primary: "bg-ws-dark text-ws-bg hover:bg-ws-accentHover",
    accent: "bg-ws-accent text-white hover:bg-ws-accentHover",
    outline: "border border-ws-dark text-ws-dark hover:bg-ws-dark hover:text-ws-bg",
    ghost: "border border-ws-border text-ws-dark hover:border-ws-dark bg-transparent",
    light: "bg-ws-bg text-ws-dark hover:bg-white",
  };
  const cls = `${base} ${styles[variant]} ${className}`;
  const inner = (
    <>
      <span>{children}</span>
      <ArrowRight className="w-4 h-4 shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
    </>
  );
  if (to) return <Link to={to} data-testid={testid} className={cls} onClick={onClick}>{inner}</Link>;
  if (href) return <a href={href} data-testid={testid} className={cls}>{inner}</a>;
  return <button type={type || "button"} data-testid={testid} className={cls} onClick={onClick}>{inner}</button>;
};

export const Eyebrow = ({ children, className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <span className="w-8 h-px shrink-0 bg-ws-accent" />
    <span className="label text-ws-accent">{children}</span>
  </div>
);

export const Reveal = ({ children, delay = 0, y = 28, className = "" }) => {
  const reducedMotion = useReducedMotion();
  return (
    <motion.div
      className={`min-w-0 ${className}`}
      initial={reducedMotion ? false : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-24px" }}
      transition={{ duration: reducedMotion ? 0 : 0.8, ease, delay: reducedMotion ? 0 : delay }}
    >
      {children}
    </motion.div>
  );
};

export const SectionHeading = ({ eyebrow, title, sub, dark = false, align = "left", className = "" }) => (
  <div className={`${align === "center" ? "text-center mx-auto items-center" : ""} flex flex-col ${className}`} style={{ maxWidth: align === "center" ? 780 : "none" }}>
    {eyebrow && <Reveal><Eyebrow className={align === "center" ? "justify-center" : ""}>{eyebrow}</Eyebrow></Reveal>}
    <Reveal delay={0.05}>
      <h2 className={`font-display mt-6 text-balance text-3xl sm:text-4xl lg:text-[3.4rem] leading-[1.08] tracking-tight ${dark ? "text-ws-bg" : "text-ws-dark"}`}>
        {title}
      </h2>
    </Reveal>
    {sub && (
      <Reveal delay={0.1}>
        <p className={`mt-6 text-base sm:text-lg leading-relaxed max-w-2xl ${align === "center" ? "mx-auto" : ""} ${dark ? "text-ws-bg/70" : "text-ws-text2"}`}>{sub}</p>
      </Reveal>
    )}
  </div>
);

export const Section = ({ children, className = "", dark = false, bg, id }) => (
  <section id={id} className={`px-5 sm:px-8 lg:px-14 py-16 sm:py-24 lg:py-32 ${className}`}
    style={{ background: dark ? "var(--ws-dark)" : bg }}>
    <div className="max-w-[1400px] mx-auto">{children}</div>
  </section>
);

export const Marquee = ({ items, dark = false }) => (
  <div className={`overflow-hidden border-y ${dark ? "border-ws-bg/15 bg-ws-dark" : "border-ws-border bg-ws-bg2"} py-6`}>
    <div className="marquee-track">
      {[...items, ...items].map((it, i) => (
        <span key={i} className={`font-display text-2xl sm:text-3xl px-8 flex items-center gap-8 whitespace-nowrap ${dark ? "text-ws-bg/80" : "text-ws-dark"}`}>
          {it}<span className="text-ws-accent text-lg">✦</span>
        </span>
      ))}
    </div>
  </div>
);

export const CTASection = () => (
  <section className="px-5 sm:px-8 lg:px-14 py-24 lg:py-32" style={{ background: "var(--ws-dark)" }}>
    <div className="max-w-[1400px] mx-auto">
      <Reveal>
        <p className="label text-ws-accent">Let's build something worth putting on the shelf</p>
      </Reveal>
      <div className="mt-8 flex flex-col xl:flex-row xl:items-end xl:justify-between gap-10">
        <Reveal delay={0.05}>
          <h2 className="font-display text-ws-bg text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight max-w-3xl text-balance">
            You have the idea. We help build the product.
          </h2>
        </Reveal>
        <Reveal delay={0.12} className="flex flex-col sm:flex-row gap-4 shrink-0">
          <Button to="/request-a-quote" variant="light" testid="cta-request-quote">Request a Quote</Button>
          <Button to="/about" variant="ghost" className="text-ws-bg border-ws-bg/30 hover:bg-ws-bg hover:text-ws-dark" testid="cta-talk-team">Talk to Our Team</Button>
        </Reveal>
      </div>
    </div>
  </section>
);

export const ArrowLink = ({ to, children, testid, dark = false }) => (
  <Link to={to} data-testid={testid} className={`group inline-flex items-center gap-1.5 label ${dark ? "text-ws-bg" : "text-ws-dark"}`}>
    <span className="link-underline">{children}</span>
    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </Link>
);
